/**
 * 画布节点右键菜单
 *
 * 功能：复制节点、删除节点、断开连线。
 * 使用 createPortal 渲染到 document.body，避免被节点 overflow 裁剪。
 * 点击菜单外部或按 Escape 关闭。
 */
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clapperboard, Copy, Info, Layers, RotateCw, Trash2, Unplug } from 'lucide-react';
import { boardActions, getBoardState } from './state/boardStore';
import { nid } from './utils';
import { extractFrameToImageNode } from './utils/imageDerive';
import { retryImageNode } from './generators/imageGenerator';
import { aggregateSelectedImageNodes } from './generators/groupOps';
import { aggregateSelectedVideoNodes } from './generators/videoGroupOps';
import { deleteNodesWithUnitGuard } from './generators/agentUnit';
import type { CanvasNode, ImageNodeData } from './types';
import { useMenuExternalDismiss } from './hooks/useMenuExternalDismiss';
import toast from '../../components/Toast';

interface ContextMenuState {
  x: number;
  y: number;
  nodeId: string;
}

interface ContextMenuProps {
  state: ContextMenuState;
  onClose: () => void;
  /** 打开节点信息弹窗（由 NodeWrapper 提供） */
  onInfo?: () => void;
}

/** 读取节点内正在播放的 <video> 的当前时间，用于"抽当前帧" */
function getVideoCurrentTime(nodeId: string): number {
  const card = document.querySelector(`[data-canvas-node-id="${nodeId}"]`);
  const video = card?.querySelector('video');
  return video && Number.isFinite(video.currentTime) ? video.currentTime : 0;
}

export function ContextMenu({ state, onClose, onInfo }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  // 点击外部 / Escape / 画布缩放时关闭（见 useMenuExternalDismiss 注释）
  useMenuExternalDismiss(menuRef, onClose);

  const cur = getBoardState();
  const node = cur.nodes.find((n) => n.id === state.nodeId);
  if (!node) return null;

  const connectedEdges = cur.edges.filter(
    (e) => e.sourceNodeId === state.nodeId || e.targetNodeId === state.nodeId
  );

  const handleDuplicate = () => {
    const newNode: CanvasNode = {
      ...node,
      id: nid(),
      x: node.x + 36,
      y: node.y + 36,
    };
    boardActions.addNode(newNode);
    onClose();
  };

  const handleDelete = () => {
    // 冻结单元守卫：节点属于智能体批次时确认整批删除（含外框），取消不删
    void deleteNodesWithUnitGuard([state.nodeId]);
    onClose();
  };

  const handleDisconnect = () => {
    if (connectedEdges.length > 0) {
      const { removed, blocked } = boardActions.removeEdges(connectedEdges.map((e) => e.id));
      if (blocked > 0) {
        toast.error(
          removed > 0
            ? '已断开可删连线；智能体批次的血缘连线不可手动断开'
            : '该节点的连线均为智能体批次血缘连线，不可手动断开，如需删除请整体删除该批次'
        );
      }
    }
    onClose();
  };

  // 视频抽帧：首帧 / 末帧 / 当前帧 → 新建独立图片节点（不连线：视频节点无图片类下游）
  const videoUrl = node.type === 'video' ? ((node.data as { videoUrl?: string | null }).videoUrl ?? null) : null;
  const canExtractFrames = node.type === 'video' && !!videoUrl;
  const handleExtractFrame = (kind: 'first' | 'last' | 'current') => {
    if (!videoUrl) return;
    const duration = (document.querySelector(`[data-canvas-node-id="${state.nodeId}"] video`) as HTMLVideoElement | null)?.duration;
    const seekTime =
      kind === 'first' ? 0 : kind === 'last' ? Math.max(0, (duration || 0) - 0.05) : getVideoCurrentTime(state.nodeId);
    onClose();
    void extractFrameToImageNode(node, videoUrl, seekTime, kind);
  };

  // 图片单张重试：上游存在 generate_image 节点时可用
  const canRetryImage =
    node.type === 'image' &&
    cur.edges.some((e) => {
      if (e.targetNodeId !== node.id) return false;
      const source = cur.nodes.find((n) => n.id === e.sourceNodeId);
      return source?.type === 'generate_image';
    });
  const handleRetryImage = () => {
    onClose();
    void retryImageNode(node as CanvasNode<ImageNodeData>).catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : '重试失败');
    });
  };

  // 聚合为图片组：当前选区（含本节点）里有 ≥2 个图片节点时可用
  const canAggregateImages =
    cur.selection.nodeIds.includes(state.nodeId) &&
    cur.nodes.filter((n) => cur.selection.nodeIds.includes(n.id) && n.type === 'image').length >= 2;

  // 合并为视频组：当前选区（含本节点）里有 ≥2 个视频节点时可用
  const canAggregateVideos =
    cur.selection.nodeIds.includes(state.nodeId) &&
    cur.nodes.filter((n) => cur.selection.nodeIds.includes(n.id) && n.type === 'video').length >= 2;

  // 确保菜单不超出视口
  const menuWidth = 180;
  const menuHeight = 140;
  const x = Math.min(state.x, window.innerWidth - menuWidth - 8);
  const y = Math.min(state.y, window.innerHeight - menuHeight - 8);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[10001] min-w-[180px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-950/10"
      style={{ left: x, top: y }}
    >
      <MenuItem
        icon={<Copy className="h-4 w-4" />}
        label="复制节点"
        shortcut="Ctrl+C"
        onClick={handleDuplicate}
      />
      {connectedEdges.length > 0 && (
        <MenuItem
          icon={<Unplug className="h-4 w-4" />}
          label={`断开连线 (${connectedEdges.length})`}
          onClick={handleDisconnect}
        />
      )}
      {onInfo && (
        <MenuItem icon={<Info className="h-4 w-4" />} label="节点信息" onClick={onInfo} />
      )}
      {canExtractFrames && (
        <>
          <div className="my-1 h-px bg-slate-100" />
          <MenuItem
            icon={<Clapperboard className="h-4 w-4" />}
            label="抽取首帧"
            onClick={() => handleExtractFrame('first')}
          />
          <MenuItem
            icon={<Clapperboard className="h-4 w-4" />}
            label="抽取末帧"
            onClick={() => handleExtractFrame('last')}
          />
          <MenuItem
            icon={<Clapperboard className="h-4 w-4" />}
            label="抽取当前帧"
            onClick={() => handleExtractFrame('current')}
          />
        </>
      )}
      {canRetryImage && (
        <>
          <div className="my-1 h-px bg-slate-100" />
          <MenuItem
            icon={<RotateCw className="h-4 w-4" />}
            label="重新生成本图"
            onClick={handleRetryImage}
          />
        </>
      )}
      {canAggregateImages && (
        <>
          <div className="my-1 h-px bg-slate-100" />
          <MenuItem
            icon={<Layers className="h-4 w-4" />}
            label="聚合为图片组"
            onClick={() => {
              onClose();
              aggregateSelectedImageNodes(cur.selection.nodeIds);
            }}
          />
        </>
      )}
      {canAggregateVideos && (
        <>
          <div className="my-1 h-px bg-slate-100" />
          <MenuItem
            icon={<Layers className="h-4 w-4" />}
            label="合并为视频组"
            onClick={() => {
              onClose();
              aggregateSelectedVideoNodes(cur.selection.nodeIds);
            }}
          />
        </>
      )}
      <div className="my-1 h-px bg-slate-100" />
      <MenuItem
        icon={<Trash2 className="h-4 w-4" />}
        label="删除节点"
        danger
        onClick={handleDelete}
      />
    </div>,
    document.body
  );
}

function MenuItem({
  icon,
  label,
  shortcut,
  danger,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition ${
        danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      <span className={danger ? 'text-red-500' : 'text-slate-400'}>{icon}</span>
      <span className="flex-1">{label}</span>
      {shortcut && (
        <span className="text-xs text-slate-400">{shortcut}</span>
      )}
    </button>
  );
}

/**
 * Hook：管理右键菜单状态。
 * 返回 menuState 和 onContextMenu 处理函数。
 */
export function useContextMenu() {
  const [menuState, setMenuState] = useState<ContextMenuState | null>(null);

  const onContextMenu = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuState({ x: e.clientX, y: e.clientY, nodeId });
  }, []);

  const closeMenu = useCallback(() => setMenuState(null), []);

  return { menuState, onContextMenu, closeMenu };
}
